// src/pages/Blog.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import Navbar from '../components/common/NavBar';
import MiniHero from '../components/common/MiniHero';
import Footer from '../components/common/Footer';
import { get } from '../utils/api';
import { getUserLanguage } from '../utils/language';
import { formatDate } from '../utils/formatters';
import { IMAGE_BASE_URL } from '../config';

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const language = getUserLanguage();
  
  const { ref: sectionRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        
        // Build query params
        const params = new URLSearchParams();
        params.append('page', currentPage);
        params.append('limit', 9); // 9 posts per page
        
        if (selectedCategory) {
          params.append('category', selectedCategory);
        }
        
        if (selectedTag) {
          params.append('tag', selectedTag);
        }
        
        const headers = { 'accept-language': language };
        const response = await get(`/blog?${params.toString()}`, headers);
        
        setPosts(response.posts || []);
        setTotalPages(response.pagination?.totalPages || 1);
        
        // Fetch categories and tags if not loaded yet
        if (categories.length === 0) {
          const categoriesData = await get('/blog/categories/list', headers);
          setCategories(categoriesData || []);
        }
        
        if (tags.length === 0) {
          const tagsData = await get('/blog/tags/list', headers);
          setTags(tagsData || []);
        }
        
        setError(null);
      } catch (error) {
        console.error('[Blog] Error fetching posts:', error);
        setError(language === 'cs' ? 'Nepodařilo se načíst příspěvky' : 'Failed to load posts');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPosts();
  }, [currentPage, selectedCategory, selectedTag, language]);
  
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1); // Reset to first page when changing category
  };
  
  const handleTagChange = (tag) => {
    setSelectedTag(tag);
    setCurrentPage(1); // Reset to first page when changing tag
  };
  
  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  if (loading && currentPage === 1) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Blog' : 'Blog'} />
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-600"></div>
        </div>
        <Footer />
      </div>
    );
  }
  
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="flex justify-center mt-12 mb-8">
        <div className="flex space-x-2">
          {currentPage > 1 && (
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              className="px-4 py-2 bg-dark-gray text-white rounded hover:bg-red-600 transition-colors"
            >
              {language === 'cs' ? 'Předchozí' : 'Previous'}
            </button>
          )}
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-4 py-2 rounded ${
                currentPage === page
                  ? 'bg-red-600 text-white'
                  : 'bg-dark-gray text-white hover:bg-red-600 transition-colors'
              }`}
            >
              {page}
            </button>
          ))}
          
          {currentPage < totalPages && (
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              className="px-4 py-2 bg-dark-gray text-white rounded hover:bg-red-600 transition-colors"
            >
              {language === 'cs' ? 'Další' : 'Next'}
            </button>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div className="min-h-screen bg-black">
      <Navbar language={language} />
      <MiniHero title={language === 'cs' ? 'Blog' : 'Blog'} />
      
      <section className="py-16 bg-black" ref={sectionRef}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main content */}
            <div className="lg:w-3/4">
              {error && (
                <div className="text-red-500 mb-6">{error}</div>
              )}
              
              {/* Filters (mobile) */}
              <div className="lg:hidden mb-8">
                <div className="mb-4">
                  <label className="text-white mb-2 block">
                    {language === 'cs' ? 'Kategorie' : 'Categories'}
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="w-full bg-dark-gray text-white p-2 rounded"
                  >
                    <option value="">{language === 'cs' ? 'Všechny kategorie' : 'All categories'}</option>
                    {categories.map((category) => (
                      <option key={category.category} value={category.category}>
                        {category.category} ({category.post_count})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="text-white mb-2 block">
                    {language === 'cs' ? 'Tagy' : 'Tags'}
                  </label>
                  <select
                    value={selectedTag}
                    onChange={(e) => handleTagChange(e.target.value)}
                    className="w-full bg-dark-gray text-white p-2 rounded"
                  >
                    <option value="">{language === 'cs' ? 'Všechny tagy' : 'All tags'}</option>
                    {tags.map((tag) => (
                      <option key={tag.name} value={tag.name}>
                        {tag.name} ({tag.count})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post, index) => (
                  <div
                    key={post.id}
                    className={`drift-card ${inView ? 'animate-fade-in' : 'opacity-0'}`}
                    style={{ animationDelay: `${0.1 * index}s` }}
                  >
                    <Link to={`/blog/${post.slug}`} className="block">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={post.image_path ? `${IMAGE_BASE_URL}${post.image_path}` : '/images/blog/placeholder.jpg'}
                          alt={post.title || 'Blog post'}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/images/blog/placeholder.jpg';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent"></div>
                      </div>
                    </Link>
                    <div className="p-6">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm text-gray-400">
                          {formatDate(post.published_date)}
                        </span>
                        {post.category && (
                          <span className="text-xs bg-red-600/20 text-red-500 px-2 py-1 rounded">
                            {post.category}
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-title font-bold text-white group-hover:text-red-500 transition-colors mb-2">
                        {post.title}
                      </h3>
                      <div className="gradient-line mb-4"></div>
                      <p className="text-gray-400 text-sm mb-4 h-16 overflow-hidden">
                        {post.summary}
                      </p>
                      <Link
                        to={`/blog/${post.slug}`}
                        className="text-red-500 hover:text-red-600 transition-colors text-sm font-bold"
                      >
                        {language === 'cs' ? 'Číst více' : 'Read more'} →
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
              
              {posts.length === 0 && !loading && (
                <div className="text-center py-12 text-gray-400">
                  {language === 'cs' ? 'Žádné příspěvky nebyly nalezeny.' : 'No posts found.'}
                </div>
              )}
              
              {/* Pagination */}
              {renderPagination()}
            </div>
            
            {/* Sidebar */}
            <div className="lg:w-1/4">
              <div className="bg-dark-gray p-6 rounded shadow-lg mb-8">
                <h3 className="text-xl font-title font-bold text-white mb-4">
                  {language === 'cs' ? 'Kategorie' : 'Categories'}
                </h3>
                <div className="gradient-line mb-4"></div>
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={() => handleCategoryChange('')}
                      className={`text-sm ${
                        selectedCategory === '' ? 'text-red-500 font-bold' : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      {language === 'cs' ? 'Všechny kategorie' : 'All categories'}
                    </button>
                  </li>
                  {categories.map((category) => (
                    <li key={category.category}>
                      <button
                        onClick={() => handleCategoryChange(category.category)}
                        className={`text-sm ${
                          selectedCategory === category.category ? 'text-red-500 font-bold' : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        {category.category} ({category.post_count})
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-dark-gray p-6 rounded shadow-lg">
                <h3 className="text-xl font-title font-bold text-white mb-4">
                  {language === 'cs' ? 'Tagy' : 'Tags'}
                </h3>
                <div className="gradient-line mb-4"></div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleTagChange('')}
                    className={`text-sm px-3 py-1 rounded-full ${
                      selectedTag === ''
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {language === 'cs' ? 'Vše' : 'All'}
                  </button>
                  {tags.map((tag) => (
                    <button
                      key={tag.name}
                      onClick={() => handleTagChange(tag.name)}
                      className={`text-sm px-3 py-1 rounded-full ${
                        selectedTag === tag.name
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {tag.name} ({tag.count})
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Blog;